import {
  Box,
  Text,
  VStack,
  HStack,
  Textarea,
  SimpleGrid,
  chakra,
} from '@chakra-ui/react';
import {
  HiExclamationTriangle,
  HiShieldExclamation,
  HiEye,
  HiHeart,
  HiCheckCircle,
  HiMapPin,
} from 'react-icons/hi2';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@themes/aegix';
import { OnePageHeader as PageHeader } from '@/components/layout/OnePageHeader';
import { useNotificationsDrawer } from '@/contexts/NotificationsDrawerContext';
import { useUnreadCount } from '@/hooks/useReunification';
import { useReportForm } from '@/hooks/useReportForm';
import { reportSchema } from '@/validation/report';
import type { ReportFormValues } from '@/validation/report';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

type IncidentCategory =
  | 'THREAT'
  | 'SUSPICIOUS_ACTIVITY'
  | 'MEDICAL'
  | 'HAZARD'
  | 'OTHER';

export default function Report() {
  const { t } = useTranslation();
  useDocumentTitle(t('report.documentTitle'));
  const { open: openNotifications } = useNotificationsDrawer();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.unread_count ?? 0;
  const {
    submitted,
    submitting,
    error,
    handleSubmit: onSubmit,
    handleReset: onReset,
  } = useReportForm();

  const categories: {
    value: IncidentCategory;
    label: string;
    description: string;
    icon: React.ElementType;
    color: string;
    bg: string;
  }[] = [
    {
      value: 'THREAT',
      label: t('report.cat.THREAT.label'),
      description: t('report.cat.THREAT.desc'),
      icon: HiExclamationTriangle,
      color: 'red.600',
      bg: 'red.50',
    },
    {
      value: 'SUSPICIOUS_ACTIVITY',
      label: t('report.cat.SUSPICIOUS_ACTIVITY.label'),
      description: t('report.cat.SUSPICIOUS_ACTIVITY.desc'),
      icon: HiEye,
      color: 'orange.600',
      bg: 'orange.50',
    },
    {
      value: 'MEDICAL',
      label: t('report.cat.MEDICAL.label'),
      description: t('report.cat.MEDICAL.desc'),
      icon: HiHeart,
      color: 'blue.600',
      bg: 'blue.50',
    },
    {
      value: 'HAZARD',
      label: t('report.cat.HAZARD.label'),
      description: t('report.cat.HAZARD.desc'),
      icon: HiShieldExclamation,
      color: 'yellow.700',
      bg: 'yellow.50',
    },
  ];

  const methods = useForm<ReportFormValues>({
    resolver: zodResolver(reportSchema),
    mode: 'onBlur',
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
  } = methods;

  const selectedCategory = watch('category');

  const handleReset = () => {
    reset();
    onReset();
  };

  if (submitted) {
    return (
      <Box>
        <PageHeader
          icon={HiExclamationTriangle}
          title={t('report.title')}
          subtitle={t('report.subtitle')}
          showSearch={false}
          showNotifications={false}
          showColorMode={false}
        />
        <Box p={{ base: 4, md: 6 }} textAlign="center">
          <Box
            mx="auto"
            mb={4}
            p={4}
            borderRadius="full"
            bg="green.100"
            color="green.600"
            display="inline-flex"
          >
            <HiCheckCircle size={40} />
          </Box>
          <Text fontSize="lg" fontWeight="600" color="text.primary" mb={2}>
            {t('report.submittedTitle')}
          </Text>
          <Text fontSize="sm" color="text.secondary" mb={6}>
            {t('report.submittedDesc')}
          </Text>
          <Button onClick={handleReset}>{t('report.submitAnother')}</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        icon={HiExclamationTriangle}
        title={t('report.title')}
        subtitle={t('report.subtitle')}
        showSearch={false}
        showNotifications={true}
        onNotificationClick={openNotifications}
        notificationCount={unreadCount}
        showColorMode={false}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box p={{ base: 4, md: 6 }}>
          {/* Category selection */}
          <Text fontSize="sm" fontWeight="600" color="text.primary" mb={3}>
            {t('report.question')}
          </Text>
          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                gap={3}
                mb={1}
                role="radiogroup"
                aria-label="Incident category"
              >
                {categories.map((cat, idx) => {
                  const Icon = cat.icon;
                  const isSelected = field.value === cat.value;
                  return (
                    <Box
                      key={cat.value}
                      as="button"
                      {...{ type: 'button' } as Record<string, string>}
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={isSelected || (!field.value && idx === 0) ? 0 : -1}
                      p={4}
                      borderRadius="lg"
                      border="2px solid"
                      borderColor={isSelected ? 'button.primary' : 'border.secondary'}
                      bg={isSelected ? 'surface.menuSelect' : 'surface.primary'}
                      cursor="pointer"
                      transition="all 200ms"
                      _hover={{ borderColor: 'button.primary' }}
                      onClick={() => field.onChange(cat.value)}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        const len = categories.length;
                        let next = -1;
                        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                          next = (idx + 1) % len;
                        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                          next = (idx - 1 + len) % len;
                        }
                        if (next >= 0) {
                          e.preventDefault();
                          field.onChange(categories[next].value);
                          const parent = (e.target as HTMLElement).parentElement;
                          const buttons = parent?.querySelectorAll<HTMLElement>('[role="radio"]');
                          buttons?.[next]?.focus();
                        }
                      }}
                      textAlign="left"
                      w="100%"
                    >
                      <HStack gap={3}>
                        <Box p={2} borderRadius="lg" bg={cat.bg} color={cat.color}>
                          <Icon size={22} />
                        </Box>
                        <Box>
                          <Text fontSize="sm" fontWeight="600" color="text.primary">
                            {cat.label}
                          </Text>
                          <Text fontSize="xs" color="text.secondary">
                            {cat.description}
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            )}
          />
          {errors.category && (
            <Text fontSize="xs" color="red.500" mt={1} mb={4}>
              {t(errors.category.message!)}
            </Text>
          )}
          {!errors.category && <Box mb={6} />}

          {/* Description */}
          {selectedCategory && (
            <VStack gap={4} align="stretch">
              <Box>
                <chakra.label htmlFor="report-description" fontSize="sm" fontWeight="600" color="text.primary" mb={2} display="block">
                  {t('report.descriptionLabel')}
                </chakra.label>
                <Textarea
                  id="report-description"
                  aria-invalid={!!errors.description}
                  aria-describedby={errors.description ? 'report-description-error' : undefined}
                  {...register('description')}
                  placeholder={t('report.descriptionPlaceholder')}
                  rows={4}
                  borderColor="border.secondary"
                  _focus={{ borderColor: 'button.primary' }}
                />
                {errors.description && (
                  <Text id="report-description-error" role="alert" fontSize="xs" color="red.500" mt={1}>
                    {t(errors.description.message!)}
                  </Text>
                )}
              </Box>

              {/* Location */}
              <Box>
                <HStack gap={2} mb={2}>
                  <HiMapPin size={16} />
                  <chakra.label htmlFor="report-location" fontSize="sm" fontWeight="600" color="text.primary">
                    {t('report.locationLabel')}
                  </chakra.label>
                </HStack>
                <Textarea
                  id="report-location"
                  {...register('location')}
                  placeholder={t('report.locationPlaceholder')}
                  rows={2}
                  borderColor="border.secondary"
                  _focus={{ borderColor: 'button.primary' }}
                />
              </Box>

              {error && (
                <Text role="alert" aria-live="assertive" color="red.500" fontSize="sm">
                  {t('report.submitError')}
                </Text>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={!isValid || submitting}
                width="100%"
              >
                {t('report.submit')}
              </Button>
            </VStack>
          )}
        </Box>
      </form>
    </Box>
  );
}
